export function normalizeDiscount(discount?: number | null): number | undefined {
  if (discount == null || discount <= 0) return undefined;
  return Math.min(100, Math.max(0, discount));
}

export function calculateSalePriceFromDiscount(
  regularPrice?: number,
  discount?: number | null,
): number | undefined {
  const normalizedDiscount = normalizeDiscount(discount);
  if (!normalizedDiscount || !regularPrice || regularPrice <= 0) return undefined;

  return Math.round(regularPrice * (1 - normalizedDiscount / 100));
}

export function calculateDiscountFromPrices(
  regularPrice?: number,
  salePrice?: number | null,
): number | undefined {
  if (!regularPrice || regularPrice <= 0 || salePrice == null || salePrice >= regularPrice) {
    return undefined;
  }

  return Math.round((1 - salePrice / regularPrice) * 10000) / 100;
}

export function syncProductPricing<T extends {
  regularPrice?: number;
  salePrice?: number;
  discount?: number;
}>(
  values: T,
  changed: 'regularPrice' | 'salePrice' | 'discount',
  nextValue?: number | null,
): T {
  const next = { ...values };

  if (changed === 'discount') {
    next.discount = normalizeDiscount(nextValue);
    next.salePrice = calculateSalePriceFromDiscount(next.regularPrice, next.discount);
    return next;
  }

  if (changed === 'regularPrice') {
    next.regularPrice = Math.round(Number(nextValue ?? 0));
    if (next.discount != null && next.discount > 0) {
      next.salePrice = calculateSalePriceFromDiscount(next.regularPrice, next.discount);
    } else {
      next.discount = calculateDiscountFromPrices(next.regularPrice, next.salePrice);
    }
    return next;
  }

  next.salePrice =
    nextValue == null || nextValue <= 0 ? undefined : Math.round(Number(nextValue));
  next.discount = calculateDiscountFromPrices(next.regularPrice, next.salePrice);
  return next;
}
